import { DateTime } from "luxon";
import { TPaymentMethod } from "../codecs/PaymentMethod";
import * as util from "../../shared/src/util";

export type TExchangeDeadlineCountFrom = "offer_accepted" | "grant_received";

export type TExchangeDeadlineRule = {
    days: number;
    countFrom: TExchangeDeadlineCountFrom;
};

export const getExchangeDeadlineRule = (args: {
    isProbate: boolean;
    buyerPaymentMethod: TPaymentMethod;
    grantReceivedBeforeOfferAccepted: boolean;
}): TExchangeDeadlineRule | null => {
    if (!args.isProbate) {
        return null;
    }

    if (!args.grantReceivedBeforeOfferAccepted) {
        return { days: 14, countFrom: "grant_received" };
    }

    switch (args.buyerPaymentMethod) {
        case "cash":
            return { days: 14, countFrom: "offer_accepted" };
        case "mortgage":
        case "bridging_loan":
        case "other":
        case "unknown":
            return { days: 28, countFrom: "offer_accepted" };
        default:
            return util.requireExhaustive(args.buyerPaymentMethod);
    }
};

export const getExchangeDeadlineWording = (rule: TExchangeDeadlineRule): string =>
    `${rule.days} days from ${
        rule.countFrom === "offer_accepted"
            ? "the acceptance of the offer"
            : "the grant being received"
    }`;

export const getExchangeDeadlineDate = (args: {
    isProbate: boolean;
    buyerPaymentMethod: TPaymentMethod;
    offerAcceptedAt: string | null;
    grantReceivedDate: string | null;
}): string | null => {
    if (args.offerAcceptedAt === null) {
        return null;
    }

    const offerAccepted = DateTime.fromISO(args.offerAcceptedAt, { zone: "utc" });
    const grantReceived = args.grantReceivedDate === null
        ? null
        : DateTime.fromISO(args.grantReceivedDate, { zone: "utc" });

    if (!offerAccepted.isValid || (grantReceived !== null && !grantReceived.isValid)) {
        return null;
    }

    const rule = getExchangeDeadlineRule({
        isProbate: args.isProbate,
        buyerPaymentMethod: args.buyerPaymentMethod,
        grantReceivedBeforeOfferAccepted:
            grantReceived !== null && grantReceived.startOf("day") <= offerAccepted.startOf("day"),
    });

    if (rule === null) {
        return null;
    }

    const countFrom = rule.countFrom === "offer_accepted" ? offerAccepted : grantReceived;

    return countFrom === null
        ? null
        : countFrom.startOf("day").plus({ days: rule.days }).toFormat("yyyy-MM-dd");
};
